import { Stack } from '@mui/material'
import { useEffect, useState, Suspense, PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'
import { track } from '@omnea-app/fe-analytics/useAnalytics'
import { MobileOverlay } from '@omnea-app/fe-app-shell/MobileOverlay'
import { Sidebar } from '@omnea-app/fe-app-shell/Sidebar'
import { useIsFullyAuthenticated, useIsSupplier } from '@omnea-app/fe-auth/UserSessionProvider'
import { useDetectDevice, useIsMobileWidth } from '@omnea-app/fe-browser-utils/useDetectDevice'
import { FullWidthLoadingFallback } from '@omnea-app/frontend/components/shared/Loading/components/LoadingFallbacks'
import { useIsSupportedMobileRoute } from '@omnea-app/frontend/utils/hooks/useIsSupportedMobileRoute'
import { ReactErrorBoundary } from '../../../utils/error-boundary/ReactErrorBoundary'
import { NotSupportedBanner } from '../../shared/ui/mobile-banner/NotSupportedBanner'

export const APP_SCROLLABLE_WRAPPER_ID = 'app-scrollable-wrapper'

type AppWrapperProps = PropsWithChildren<{
  disableSidebar?: boolean
  pathname: string
}>

export const AppWrapper = ({ children, disableSidebar, pathname }: AppWrapperProps) => {
  const { t } = useTranslation('shared')

  const { isMobileAgent, hasMobileEvents, isMobileWidth } = useDetectDevice()

  const isSupportedMobileRoute = useIsSupportedMobileRoute()

  useEffect(() => {
    if (isMobileWidth) {
      track('ViewedOnMobile', {
        isMobileAgent,
        hasMobileEvents,
        screenWidth: window?.screen?.width,
        screenHeight: window?.screen?.height,
        viewportWidth: window?.innerWidth,
        viewportHeight: window?.innerHeight,
      })
    }
  }, [isMobileWidth, hasMobileEvents, isMobileAgent])

  if (!isMobileWidth || isSupportedMobileRoute) {
    return (
      <Stack sx={{ height: '100vh' }} direction="row" data-testid="page-dashboard" overflow="hidden">
        <Wrapper disableSidebar={disableSidebar} pathname={pathname}>
          {children}
        </Wrapper>
      </Stack>
    )
  }

  // For desktop with unsupported width, show overlay over content to prevent rerenders
  if (!isMobileAgent) {
    const translationKey = 'mobile-width-page-not-supported-banner'
    return (
      <Stack sx={{ height: '100vh', position: 'relative' }} direction="row" data-testid="page-dashboard" overflow="hidden">
        <Wrapper disableSidebar={disableSidebar} pathname={pathname}>
          {children}
        </Wrapper>
        <MobileOverlay>
          <NotSupportedBanner title={t(`${translationKey}.title`)} description={t(`${translationKey}.subtitle`)} />
        </MobileOverlay>
      </Stack>
    )
  }

  if (pathname === '/') {
    return (
      <MobileOverlay>
        <NotSupportedBanner title={t('mobile-home-banner.title')} description={t('mobile-home-banner.subtitle')} />
      </MobileOverlay>
    )
  }

  const translationKey = 'mobile-agent-page-not-supported-banner'
  return (
    <MobileOverlay>
      <NotSupportedBanner title={t(`${translationKey}.title`)} description={t(`${translationKey}.subtitle`)} />
    </MobileOverlay>
  )
}
export const mainWrapperContainerName = 'main-content'

const Wrapper = ({ children, disableSidebar, pathname }: AppWrapperProps) => {
  const isAuthenticated = useIsFullyAuthenticated()
  const isSupplier = useIsSupplier()
  const isMobileWidth = useIsMobileWidth()
  const showSidebar = isAuthenticated && !isSupplier && !isMobileWidth && !disableSidebar
  const [isHydrated, setIsHydrated] = useState(false)

  // Mark as hydrated after initial render to avoid hydration timing issues
  useEffect(() => {
    setIsHydrated(true)
  }, [])

  return (
    <ReactErrorBoundary>
      {showSidebar && <Sidebar path={pathname} />}
      <Stack
        width="100%"
        height="100%"
        sx={{
          flex: 1,
          background: 'neutralLight.light',

          // This is used by sub-components for container media queries
          // @see https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_containment/Container_queries
          containerName: mainWrapperContainerName,
          containerType: 'inline-size',

          overflowX: 'clip',
          overflowY: 'auto',
        }}
        id={APP_SCROLLABLE_WRAPPER_ID}
      >
        {isHydrated ? <Suspense fallback={<FullWidthLoadingFallback />}>{children}</Suspense> : children}
      </Stack>
    </ReactErrorBoundary>
  )
}
